// components/layout/NotificationMenu.tsx
import React from 'react';
import {
  Menu,
  MenuItem,
  ListItemText,
  Typography,
  Box,
  Badge,
  Divider,
} from '@mui/material';
import {
  Notifications,
  NotificationsNone,
} from '@mui/icons-material';

interface NotificationMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

// Mock notifications - w prawdziwej aplikacji będą z API/Redux
const mockNotifications = [
  {
    id: 1,
    title: 'Nowy formularz do wypełnienia',
    message: 'Formularz "Kontrola jakości" czeka na obiekt "Linia 1"',
    time: '5 min temu',
    read: false,
  },
  {
    id: 2,
    title: 'Synchronizacja offline',
    message: 'Wysłano 3 formularze po odzyskaniu połączenia',
    time: '1 godz. temu',
    read: false,
  },
  {
    id: 3,
    title: 'Raport wygenerowany',
    message: 'Raport miesięczny jest gotowy do pobrania',
    time: '2 godz. temu',
    read: true,
  },
];

const NotificationMenu: React.FC<NotificationMenuProps> = ({
  anchorEl,
  open,
  onClose,
}) => {
  const unreadCount = mockNotifications.filter(n => !n.read).length;

  const handleNotificationClick = (notificationId: number) => {
    console.log('Clicked notification:', notificationId);
    // Tutaj będzie logika obsługi powiadomienia
    onClose();
  };

  const handleMarkAllRead = () => {
    console.log('Mark all notifications as read');
    // Tutaj będzie logika oznaczania jako przeczytane
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      id="notification-menu"
      open={open}
      onClose={onClose}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: 'visible',
          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
          mt: 1.5,
          minWidth: 350,
          maxWidth: 400,
          maxHeight: 400,
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Powiadomienia
          {unreadCount > 0 && (
            <Badge badgeContent={unreadCount} color="error" sx={{ ml: 1 }} />
          )}
        </Typography>
        {unreadCount > 0 && (
          <Typography 
            variant="caption" 
            color="primary" 
            sx={{ cursor: 'pointer' }}
            onClick={handleMarkAllRead}
          >
            Oznacz jako przeczytane
          </Typography>
        )}
      </Box>

      <Divider />

      {mockNotifications.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <NotificationsNone sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Brak powiadomień
          </Typography>
        </Box>
      ) : (
        mockNotifications.map((notification) => (
          <MenuItem
            key={notification.id}
            onClick={() => handleNotificationClick(notification.id)}
            sx={{
              whiteSpace: 'normal',
              backgroundColor: notification.read ? 'transparent' : 'action.hover',
              borderLeft: notification.read ? 'none' : '3px solid',
              borderLeftColor: 'primary.main',
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle2" fontWeight={notification.read ? 'normal' : 'bold'}>
                {notification.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {notification.message}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {notification.time}
              </Typography>
            </Box>
          </MenuItem>
        ))
      )}

      {mockNotifications.length > 0 && (
        <>
          <Divider />
          <MenuItem onClick={onClose} sx={{ justifyContent: 'center' }}>
            <Typography variant="body2" color="primary">
              Zobacz wszystkie powiadomienia
            </Typography>
          </MenuItem>
        </>
      )}
    </Menu>
  );
};

export default NotificationMenu;